/**
 * useClusterApi - Platform-Aware Clustering Operations
 *
 * Routes clustering operations to:
 * - Electron: Local PostgreSQL + pgvector + HDBSCAN
 * - Web/Cloud: HTTP API to Workers AI
 *
 * Follows the useArchiveApi pattern for stability:
 * - Uses refs for non-render state
 * - Callbacks read from refs at call time
 * - Memoized return object
 *
 * @module @humanizer/studio/hooks/useClusterApi
 */

import { useCallback, useMemo } from 'react';
import { usePlatformRouter, type Backend } from './usePlatformRouter';
import type { PlatformContextValue } from '../contexts/PlatformContext';
import type {
  ElectronClusterSummary,
  ElectronClusterDetail,
  ElectronClusterDraftResult,
} from '../types/electron';

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

/** Cluster summary for listing */
export interface ClusterSummary {
  id: string;
  label: string;
  description?: string;
  nodeCount: number;
  quality: number;
  keywords?: string[];
  createdAt?: string;
  updatedAt?: string;
}

/** Passage within a cluster */
export interface ClusterPassage {
  id: string;
  text: string;
  distanceFromCentroid?: number;
  wordCount?: number;
  sourceType?: string;
  authorRole?: string;
}

/** Full cluster details */
export interface ClusterDetail {
  id: string;
  label: string;
  description?: string;
  passages: ClusterPassage[];
  totalPassages: number;
  coherence: number;
  keywords?: string[];
}

/** Draft generation result */
export interface ClusterDraftResult {
  draft: string;
  wordCount: number;
  passageCount: number;
  clusterLabel: string;
  arc?: {
    type: string;
    segments: Array<{ position: number; text: string }>;
  };
  cullReport?: unknown;
}

/** Cluster discovery statistics */
export interface ClusterDiscoveryStats {
  totalPassages: number;
  assignedPassages: number;
  noisePassages: number;
  durationMs: number;
}

export interface ClusterApi {
  /** Whether clustering is available */
  isAvailable: boolean;
  /** Which backend will be used */
  backend: Backend;
  /** Reason if unavailable */
  unavailableReason: string | null;

  /** List existing clusters */
  listClusters: (options?: {
    limit?: number;
    forceRediscover?: boolean;
  }) => Promise<ClusterSummary[]>;

  /** Discover clusters using HDBSCAN */
  discoverClusters: (options?: {
    minClusterSize?: number;
    maxClusters?: number;
    sampleSize?: number;
    sourceTypes?: string[];
    authorRoles?: string[];
  }) => Promise<{
    clusters: ClusterSummary[];
    stats: ClusterDiscoveryStats;
  }>;

  /** Get cluster details with passages */
  getCluster: (clusterId: string) => Promise<ClusterDetail>;

  /** Generate draft from cluster */
  generateClusterDraft: (clusterId: string, options?: {
    targetWordCount?: number;
    arcType?: string;
    topN?: number;
  }) => Promise<ClusterDraftResult>;
}

// ═══════════════════════════════════════════════════════════════════════════
// HOOK
// ═══════════════════════════════════════════════════════════════════════════

export function useClusterApi(): ClusterApi {
  const { getBackend, isAvailable, backend, unavailableReason, cloudApi } =
    usePlatformRouter<{ hasLocalLLM: boolean }>({
      resolveCapabilities(platform: PlatformContextValue | null, isElectron: boolean) {
        if (platform) {
          return {
            canUseLocal: platform.isElectron && platform.canUseLocalArchive,
            canUseCloud: platform.canUseCloudLLM,
            hasLocalLLM: platform.canUseLocalLLM,
            preferredBackend: platform.isElectron && platform.canUseLocalArchive
              ? 'local' as const
              : (platform.canUseCloudLLM ? 'cloud' as const : 'none' as const),
          };
        }
        return {
          canUseLocal: isElectron,
          canUseCloud: !isElectron,
          hasLocalLLM: false,
          preferredBackend: isElectron ? 'local' as const : 'cloud' as const,
        };
      },
      resolveAvailability(platform: PlatformContextValue | null, isElectron: boolean) {
        const isAvailable = platform
          ? (platform.isElectron && platform.canUseLocalArchive) || platform.canUseCloudLLM
          : !isElectron;
        const backend = platform?.isElectron && platform?.canUseLocalArchive
          ? 'local' as const
          : (platform?.canUseCloudLLM ? 'cloud' as const : (isElectron ? 'none' as const : 'cloud' as const));
        const unavailableReason = isAvailable
          ? null
          : isElectron
            ? 'PostgreSQL not configured. Configure database connection in settings.'
            : 'Sign in to use cloud clustering.';
        return { isAvailable, backend, unavailableReason };
      },
      platformDeps: (platform) => [platform?.isElectron, platform?.canUseLocalArchive, platform?.canUseLocalLLM, platform?.canUseCloudLLM],
    });

  // ─────────────────────────────────────────────────────────────────────────
  // CLUSTER OPERATIONS
  // ─────────────────────────────────────────────────────────────────────────

  const listClusters = useCallback(async (options?: {
    limit?: number;
    forceRediscover?: boolean;
  }): Promise<ClusterSummary[]> => {
    const currentBackend = getBackend();

    if (currentBackend === 'local') {
      const electronArchive = window.electronArchive;
      if (!electronArchive?.listClusters) {
        throw new Error('Local cluster API not available');
      }
      const result = await electronArchive.listClusters(options);
      return result.clusters.map((c: ElectronClusterSummary) => ({
        id: c.id,
        label: c.label,
        description: c.description,
        nodeCount: c.nodeCount,
        quality: c.quality,
        keywords: c.keywords,
        createdAt: c.createdAt,
        updatedAt: c.updatedAt,
      }));
    }

    if (currentBackend === 'cloud') {
      const result = await cloudApi.listClusters(options);
      return result.clusters;
    }

    throw new Error(unavailableReason || 'No clustering backend available');
  }, [getBackend, unavailableReason, cloudApi]);

  const discoverClusters = useCallback(async (options?: {
    minClusterSize?: number;
    maxClusters?: number;
    sampleSize?: number;
    sourceTypes?: string[];
    authorRoles?: string[];
  }): Promise<{ clusters: ClusterSummary[]; stats: ClusterDiscoveryStats }> => {
    const currentBackend = getBackend();

    if (currentBackend === 'local') {
      const electronArchive = window.electronArchive;
      if (!electronArchive?.discoverClusters) {
        throw new Error('Local cluster API not available');
      }
      const result = await electronArchive.discoverClusters(options);
      return {
        clusters: result.clusters.map((c: ElectronClusterSummary) => ({
          id: c.id,
          label: c.label,
          description: c.description,
          nodeCount: c.nodeCount,
          quality: c.quality,
          keywords: c.keywords,
          createdAt: c.createdAt,
          updatedAt: c.updatedAt,
        })),
        stats: result.stats,
      };
    }

    if (currentBackend === 'cloud') {
      const result = await cloudApi.discoverClusters(options);
      return {
        clusters: result.clusters,
        stats: {
          totalPassages: result.totalPassages ?? 0,
          assignedPassages: result.assignedPassages ?? 0,
          noisePassages: result.noisePassages ?? 0,
          durationMs: result.durationMs ?? 0,
        },
      };
    }

    throw new Error(unavailableReason || 'No clustering backend available');
  }, [getBackend, unavailableReason, cloudApi]);

  const getCluster = useCallback(async (clusterId: string): Promise<ClusterDetail> => {
    const currentBackend = getBackend();

    if (currentBackend === 'local') {
      const electronArchive = window.electronArchive;
      if (!electronArchive?.getCluster) {
        throw new Error('Local cluster API not available');
      }
      const result: ElectronClusterDetail = await electronArchive.getCluster(clusterId);
      return {
        id: result.id,
        label: result.label,
        description: result.description,
        passages: result.passages,
        totalPassages: result.totalPassages,
        coherence: result.coherence,
        keywords: result.keywords,
      };
    }

    if (currentBackend === 'cloud') {
      return cloudApi.getCluster(clusterId);
    }

    throw new Error(unavailableReason || 'No clustering backend available');
  }, [getBackend, unavailableReason, cloudApi]);

  const generateClusterDraft = useCallback(async (
    clusterId: string,
    options?: {
      targetWordCount?: number;
      arcType?: string;
      topN?: number;
    }
  ): Promise<ClusterDraftResult> => {
    const currentBackend = getBackend();

    if (currentBackend === 'local') {
      const electronArchive = window.electronArchive;
      if (!electronArchive?.generateClusterDraft) {
        throw new Error('Local cluster draft generation not available');
      }
      const result: ElectronClusterDraftResult = await electronArchive.generateClusterDraft(
        clusterId,
        options
      );
      return {
        draft: result.draft,
        wordCount: result.wordCount,
        passageCount: result.passageCount,
        clusterLabel: result.clusterLabel,
        arc: result.arc,
      };
    }

    if (currentBackend === 'cloud') {
      return cloudApi.generateClusterDraft(clusterId, options);
    }

    throw new Error(unavailableReason || 'No clustering backend available');
  }, [getBackend, unavailableReason, cloudApi]);

  // Memoize the returned object for stability
  return useMemo(() => ({
    isAvailable,
    backend,
    unavailableReason,
    listClusters,
    discoverClusters,
    getCluster,
    generateClusterDraft,
  }), [
    isAvailable,
    backend,
    unavailableReason,
    listClusters,
    discoverClusters,
    getCluster,
    generateClusterDraft,
  ]);
}

export default useClusterApi;
